import { useEffect, useRef } from 'react'

export function useFocusTrap(
  enabled: boolean,
  containerRef: React.RefObject<HTMLElement>,
  /** When true, listens at the root level to intercept all Tab presses — use when inert is not applied to siblings */
  strict = false
) {
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!enabled) return
    previouslyFocused.current = document.activeElement as HTMLElement
    setTimeout(() => {
      containerRef.current?.focus()
    }, 0)

    const trapFocus = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const container = containerRef.current
      if (!container) return
      const allFocusable = container.querySelectorAll<HTMLElement>(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]'
      )
      const focusable = Array.from(allFocusable).filter(el => {
        // Check computed visibility — handles display:none and visibility:hidden
        const style = globalThis.getComputedStyle(el)
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false
        }
        // Walk up ancestors to check for hidden parents
        let parent = el.parentElement
        while (parent && parent !== container) {
          const parentStyle = globalThis.getComputedStyle(parent)
          if (
            parentStyle.display === 'none' ||
            parentStyle.visibility === 'hidden'
          ) {
            return false
          }
          parent = parent.parentElement
        }
        return true
      })
      if (!focusable.length) {
        e.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const root = container.getRootNode() as Document | ShadowRoot
      const active = root.activeElement

      // In strict mode, pull focus back if it's outside the container
      if (strict && (!active || !container.contains(active))) {
        e.preventDefault()
        first.focus()
        return
      }

      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    if (strict) {
      // Listen at root level to catch Tab even when focus is outside the container
      const container = containerRef.current
      const root = container?.getRootNode() as Document | ShadowRoot | undefined
      const eventTarget = root || document
      eventTarget.addEventListener('keydown', trapFocus as EventListener, true)
      return () => {
        eventTarget.removeEventListener(
          'keydown',
          trapFocus as EventListener,
          true
        )
        if (previouslyFocused.current) {
          previouslyFocused.current.focus()
        }
      }
    } else {
      // Default: listen on container only (works when inert is used on siblings)
      const container = containerRef.current
      container?.addEventListener('keydown', trapFocus)
      return () => {
        container?.removeEventListener('keydown', trapFocus)
        if (previouslyFocused.current) {
          previouslyFocused.current.focus()
        }
      }
    }
  }, [enabled, containerRef, strict])
}
